/**
 * What is configured, what is missing, and what each missing key would change.
 *
 * A greyed-out control is a bad answer on its own. When news is empty or prices
 * are labelled delayed, this says which key is responsible, what it costs, and
 * where to get it — so the state reads as unconfigured rather than broken.
 */
import type { SourcesStatus } from "@tt/shared";

export function DataSources({ status, dismissed, onDismiss }: {
  status: SourcesStatus;
  /** Symptoms the operator has chosen to live with. */
  dismissed: string[];
  onDismiss: (symptom: string, hide: boolean) => void;
}) {
  return (
    <div className="sources">
      <div className="sources-head">
        <strong>Data sources</strong>
        <span className="badge muted">
          {status.configured_count} of {status.total} configured
        </span>
      </div>
      <div className="hint">{status.headline}</div>

      {status.limitations.length > 0 && (
        <>
          <h3>What this currently limits</h3>
          {/* Some gaps are a decision rather than a fault -- choosing Alpaca
              over Polygon leaves second-level bars greyed out, permanently and
              on purpose. A warning that can never be resolved stops being read,
              and takes the ones that matter down with it. Dismissing keeps it
              here, where it is still the answer to "why is that greyed out",
              and takes it out of the topbar. */}
          {status.limitations.map((l) => {
            const hidden = dismissed.includes(l.symptom);
            return (
              <div className={`limitation ${hidden ? "dim" : ""}`} key={l.symptom}>
                <div className="limitation-symptom">{l.symptom}</div>
                <div className="faint small">{l.because}</div>
                <div className="small row" style={{ gap: 8, alignItems: "center" }}>
                  <span className="badge good">{l.fix}</span>
                  <button
                    className="btn small"
                    onClick={() => onDismiss(l.symptom, !hidden)}
                    title={
                      hidden
                        ? "Count this in the status badge again"
                        : "Keep it listed here, but stop counting it in the status badge"
                    }
                  >
                    {hidden ? "Show again" : "Accept"}
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}

      <h3>Keys</h3>
      {status.sources.map((s) => (
        <div className={`source ${s.configured ? "on" : ""}`} key={s.id}>
          <div className="row" style={{ gap: 7, alignItems: "baseline" }}>
            <span className={`badge ${s.configured ? "good" : s.required ? "warn" : "muted"}`}>
              {s.configured ? "configured" : s.required ? "recommended" : "optional"}
            </span>
            <strong className="grow">{s.name}</strong>
            <a
              className="btn small"
              href={s.configured ? s.keys_url : s.signup_url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {s.configured ? "manage keys" : "get a key"}
            </a>
          </div>
          <div className="mono faint small" style={{ margin: "3px 0" }}>
            {s.env_vars.join(", ")}
          </div>
          <ul className="tight muted small">
            {s.unlocks.map((u) => <li key={u}>{u}</li>)}
          </ul>
          <div className="faint small">{s.cost}</div>
          {s.free_tier && (
            <div className="faint small">Free tier: {s.free_tier}</div>
          )}
        </div>
      ))}

      {status.alternatives.length > 0 && (
        <>
          <h3>Alternatives</h3>
          <div className="hint">
            Checked against each vendor's own pricing page. Only the ones marked
            below have an adapter in this codebase; the rest would need one
            writing.
          </div>
          {status.alternatives.map((a) => (
            <div className="source" key={a.name}>
              <div className="row" style={{ gap: 7, alignItems: "baseline" }}>
                <span className={`badge ${a.supported ? "good" : "muted"}`}>
                  {a.supported ? "supported" : "no adapter"}
                </span>
                <strong className="grow">{a.name}</strong>
                <a
                  className="btn small"
                  href={a.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  pricing
                </a>
              </div>
              <div className="faint small" style={{ margin: "3px 0" }}>{a.covers}</div>
              <div className="small mono" style={{ color: "var(--tone-warn)" }}>
                {a.cost}
              </div>
              <div className="muted small" style={{ marginTop: 3 }}>{a.verdict}</div>
            </div>
          ))}
        </>
      )}

      <div className="notebox">
        Put keys in <span className="mono">{status.env_file}</span>
      </div>

      {/* Lightweight Charts is Apache-2.0 and asks for the copyright line from
          its NOTICE plus a visible link to tradingview.com. Meeting it here is
          what permits turning off the attribution logo the library otherwise
          paints into the corner of every chart. */}
      <h3>Credits</h3>
      <div className="muted small">
        Charts drawn with{" "}
        <a
          href="https://www.tradingview.com/lightweight-charts/"
          target="_blank"
          rel="noreferrer noopener"
        >
          Lightweight Charts
        </a>{" "}
        — Copyright 2023{" "}
        <a
          href="https://www.tradingview.com/"
          target="_blank"
          rel="noreferrer noopener"
        >
          TradingView, Inc.
        </a>{" "}
        Licensed under Apache 2.0.
      </div>
    </div>
  );
}
